import { LS_PHONE_NUMBER, LS_SELECTED_SHOP_IDS, LS_SELECTED_SHOP_NAME, LS_SHOP, LS_USERNAME } from 'constants/index';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { getShopList, getUserData, getUserRole, postGetOTP, postLogin, postValidateOTP, postValidatePhoneNumber } from 'services/auth';
import { setInitialShopState, setPermission } from 'store/reducers/shop';
import { LS_AUTH, LS_ROLES, LS_USER } from '../constants';
import { useCommonHooks } from './common';
import { useLang } from './common/lang';

export const useLoginHooks = () => {
    const [code, setCode] = useState();
    const [isRememberLogin, setIsRememberLogin] = useState(true);
    const [phoneNumber, setPhoneNumber] = useState();
    const [countryCode, setCountryCode] = useState();
    const [sessionId, setSessionId] = useState();
    const [isPinExist, setIsPinExist] = useState(false);
    const [isLoadingResend, setIsLoadingResend] = useState(false);
    const [isShowInputDialog, setIsShowInputDialog] = useState(false);

    const { $t } = useLang();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const { initiateShopInfo } = useCommonHooks();

    const handleValidatePhoneNumber = useCallback(async (phoneNumber, countryCode) => {
        try {
            const response = await postValidatePhoneNumber({
                phone_number: phoneNumber,
                country_code: countryCode
            });

            if (response.code !== 200) throw new Error(response.err_msg);

            if (response?.data?.exists) {
                setPhoneNumber(phoneNumber);
                setCountryCode(countryCode);
            }

            return response;
        } catch (e) {
            throw e;
        }
    }, []);

    const handleGetOtp = useCallback(async (phoneNumber, countryCode, channel = 'whatsapp') => {
        try {
            const { code, data, error } = await postGetOTP({
                phone_number: phoneNumber,
                country_code: countryCode,
                channel,
                purpose: 'reset_pin'
            });

            if (code !== 200) throw error;

            setSessionId(data?.session_id);

            return data;
        } catch (e) {
            throw e;
        }
    }, []);

    const handleVerifyOtp = useCallback(
        async (otpCode) => {
            try {
                const { code, data, error } = await postValidateOTP({
                    session_id: sessionId,
                    otp_code: otpCode
                });

                if (code !== 200) throw error;

                handleNavigateUpdatePin(data.token);
            } catch (e) {
                throw e;
            }
        },
        [sessionId]
    );

    const handleLoginWithPin = useCallback(
        async (pin) => {
            try {
                const { code, data, error } = await postLogin({
                    pin,
                    phone_number: phoneNumber,
                    country_code: countryCode,
                    remember_me: isRememberLogin
                });

                if (code !== 200) throw error;

                localStorage.setItem(LS_AUTH, data?.auth_token);
                localStorage.setItem(LS_USER, data?.user_id);
                setPhoneNumber(phoneNumber);
                fetchUserShopList();
                fetchUserData();
            } catch (e) {
                throw e;
            }
        },
        [phoneNumber, countryCode, isRememberLogin]
    );

    const fetchUserShopList = useCallback(async (isCreateNewShop) => {
        const response = await getShopList();
        if (response?.shops) {
            let list = [];
            let listName = [];
            response.shops.forEach((shop) => {
                list.push({
                    id: shop.id,
                    name: shop.name,
                    isVerified: shop.is_verified,
                    currency: shop.currency_data || {
                        decimal_num: 0,
                        code: 'Rp',
                        decimal: ',',
                        thousand: '.'
                    },
                    reservationCurrency: shop.reservation_currency_data || {
                        decimal_num: 0,
                        code: 'Rp',
                        decimal: ',',
                        thousand: '.'
                    },
                    coins: shop.shop_loyalty
                });
                listName.push(shop.name);
            });
            fetchUserRoles(list[0].id);
            localStorage.setItem(LS_SHOP, JSON.stringify(list));
            if (isCreateNewShop) {
                initiateShopInfo();
            }
            // dispatch(setListShop({ list }));
            // dispatch(setSelectedShop({ selected: list[0] }));
            // dispatch(setSelectedShopName({ selectedName: list[0].name }));
            // dispatch(setListShopName({ listName }));
        } else {
            // todo: handle error
        }
    }, []);

    const fetchUserData = useCallback(async () => {
        const response = await getUserData();
        if (response) {
            localStorage.setItem(LS_USERNAME, response.username ?? '');
            localStorage.setItem(LS_PHONE_NUMBER, response.phone_number);
        } else {
            // todo: handle error
        }
    }, []);

    const fetchUserRoles = useCallback(async (shopId) => {
        const response = await getUserRole();
        if (response) {
            const permission = response.filter((role) => role.shop_id === shopId)[0].dashboard_permissions;
            switch (response[0].dashboard_permissions[0]) {
                case 'dashboard.report.view':
                    navigate(`/sales`);
                    break;
                case 'dashboard.attendance.view':
                    navigate(`/attendance/history`);
                    break;
                case 'dashboard.menu.view':
                    navigate(`/products`);
                    break;
                case 'dashboard.crm.view':
                    navigate(`/buyers`);
                    break;
                case 'dashboard.inventory.view':
                    navigate(`/ingredients`);
                    break;
                case 'dashboard.table.view':
                    navigate(`/table-management/table-layout`);
                    break;
                default:
                    const permissions = response.filter((role) => role.shop_id === shopId)[0].dashboard_permissions;
                    const permission = permissions.filter((p) => p.split('.')[2] === 'view');
                    switch (permission[0]) {
                        case 'dashboard.report.view':
                            navigate(`/sales`);
                            break;
                        case 'dashboard.attendance.view':
                            navigate(`/attendance/history`);
                            break;
                        case 'dashboard.menu.view':
                            navigate(`/products`);
                            break;
                        case 'dashboard.crm.view':
                            navigate(`/buyers`);
                            break;
                        case 'dashboard.inventory.view':
                            navigate(`/ingredients`);
                            break;
                        case 'dashboard.table.view':
                            navigate(`/table-management/table-layout`);
                            break;
                        default:
                            navigate('/');
                    }
            }
            dispatch(setPermission(permission));
            localStorage.setItem(LS_ROLES, JSON.stringify(response));
        } else {
            // todo: handle error
        }
    }, []);

    const logOut = useCallback(() => {
        clearLS();
        dispatch(setInitialShopState());
        navigate(`/login`);
    }, []);

    const handleNavigateRegister = useCallback(() => {
        navigate('/register');
    }, []);

    const handleNavigateForgotPin = useCallback(() => {
        navigate('/forgot-pin');
    }, []);

    const handleNavigateUpdatePin = useCallback((token) => {
        navigate('/update-pin', { state: { token } });
    }, []);

    const clearLS = useCallback(() => {
        localStorage.removeItem(LS_AUTH);
        localStorage.removeItem(LS_SHOP);
        localStorage.removeItem(LS_USERNAME);
        localStorage.removeItem(LS_PHONE_NUMBER);
        localStorage.removeItem(LS_USER);
        localStorage.removeItem(LS_SELECTED_SHOP_NAME);
        localStorage.removeItem(LS_SELECTED_SHOP_IDS);
    }, []);

    useEffect(() => {
        if (location.pathname === '/login') {
            clearLS();
        }
    }, []);

    return {
        $t,
        phoneNumber,
        setPhoneNumber,
        countryCode,
        setCountryCode,
        code,
        setCode,
        isRememberLogin,
        setIsRememberLogin,
        isShowInputDialog,
        setIsShowInputDialog,
        isPinExist,
        setIsPinExist,
        isLoadingResend,
        setIsLoadingResend,
        fetchUserShopList,
        logOut,
        handleValidatePhoneNumber,
        handleGetOtp,
        handleVerifyOtp,
        handleLoginWithPin,
        handleNavigateRegister,
        handleNavigateForgotPin,
        handleNavigateUpdatePin
    };
};
